import {
    AdSlotBlock,
    hasAdCreative,
} from '@/components/site/ad-slot';
import { NewsletterSignup } from '@/components/site/newsletter-signup';
import {
    HOMEPAGE_SIDEBAR_COMPACT_SLOTS,
    HOMEPAGE_SIDEBAR_SLOTS,
    fillAdSlots,
} from '@/lib/ads';
import type { Ad, ExternalAlert } from '@/lib/content-types';
import { getProviderIcon } from '@/lib/provider-icons';
import { SITE_SOCIAL_LINKS } from '@/lib/site-social';
import { getTimeAgo } from '@/lib/timeUtils';
import { cn } from '@/lib/utils';

const SIDEBAR_EXTERNAL_ALERTS_LIMIT = 5;

function SidebarExternalTeasers({
    title,
    alerts = [],
}: {
    title: string;
    alerts?: ExternalAlert[];
}) {
    if (alerts.length === 0) return null;

    return (
        <section className="border-b border-white/10" aria-label={title}>
            <div className="px-6 py-3">
                <p className="text-sm text-white/55">{title}</p>
            </div>
            <ul>
                {alerts.map((alert, index) => {
                    const when = alert.publishedAt || alert.firstSeenAt;
                    const Icon = getProviderIcon(alert.provider);
                    return (
                        <li
                            key={alert.$id}
                            className={cn(
                                'px-6 py-4',
                                index < alerts.length - 1 && 'border-b border-white/10',
                            )}
                        >
                            <a
                                href={alert.url}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                className="group block"
                            >
                                <span className="flex items-center gap-2 text-sm text-white/55">
                                    {Icon ? (
                                        <Icon className="size-5 shrink-0" />
                                    ) : null}
                                    <span className="truncate">{alert.providerLabel}</span>
                                    {when ? (
                                        <>
                                            <span aria-hidden className="text-white/20">
                                                ·
                                            </span>
                                            <time dateTime={when} className="shrink-0">
                                                {getTimeAgo(when)}
                                            </time>
                                        </>
                                    ) : null}
                                </span>
                                <h3 className="title-two-line mt-1.5 text-sm font-normal leading-snug tracking-tight text-white transition-colors group-hover:text-white/85">
                                    {alert.title}
                                </h3>
                            </a>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function SocialLinks() {
    return (
        <section className="px-6 py-5" aria-label="עקבו אחרינו">
            <p className="text-sm text-white/55">עקבו אחרינו</p>
            <ul className="mt-3 space-y-2">
                {SITE_SOCIAL_LINKS.map((link) => {
                    const Icon = link.icon;
                    return (
                        <li key={link.id}>
                            <a
                                href={link.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-white/55 transition-colors hover:text-white"
                            >
                                <Icon aria-hidden className="size-4 shrink-0" />
                                {link.label}
                            </a>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

function SidebarNewsletter({ id }: { id: string }) {
    return (
        <section
            className="border-b border-white/10 px-6 py-5"
            aria-label="ניוזלטר"
        >
            <NewsletterSignup compact id={id} />
        </section>
    );
}

/**
 * Visual-left homepage rail (inline-end in RTL). Desktop column; on mobile the
 * parent can stack a compact variant after the main feed.
 *
 * Ad units come from placement slots (standard IAB sizes) filled by the ads table.
 * On desktop this rail is sticky from the top of the page beside hero + feed.
 */
export function HomeSidebar({
    talkingAboutUs = [],
    ads = [],
    compact = false,
}: {
    talkingAboutUs?: ExternalAlert[];
    ads?: Ad[];
    /** Narrower stack for mobile: fewer ad slots, same content blocks. */
    compact?: boolean;
}) {
    const slotDefs = compact ? HOMEPAGE_SIDEBAR_COMPACT_SLOTS : HOMEPAGE_SIDEBAR_SLOTS;
    const filled = fillAdSlots(slotDefs, ads);
    const coverage = (talkingAboutUs ?? []).slice(0, SIDEBAR_EXTERNAL_ALERTS_LIMIT);

    const [top, ...rest] = filled;
    const bottom = !compact && rest.length > 0 ? rest[rest.length - 1] : undefined;
    const middle = compact ? rest : rest.slice(0, Math.max(0, rest.length - 1));
    const showTopAd = Boolean(top && hasAdCreative(top));

    return (
        <aside className="bg-black" aria-label="תוכן נוסף ומודעות">
            {showTopAd && top && <AdSlotBlock filled={top} />}

            {middle.map((item) => (
                <AdSlotBlock key={item.slot.id} filled={item} />
            ))}

            <SidebarExternalTeasers title="מדברים עלינו" alerts={coverage} />

            {bottom && <AdSlotBlock filled={bottom} />}

            <SidebarNewsletter
                id={compact ? 'newsletter-email-sidebar-mobile' : 'newsletter-email-sidebar'}
            />

            <SocialLinks />
        </aside>
    );
}
